// ═══════════════════════════════════════════════════════════════════
// motionKit.js — THE AUTHORING KIT · H24 · Terror TCG
// One palette, one set of chord builders, one linter. Every score file
// in the game imports this and nothing else.
// ═══════════════════════════════════════════════════════════════════
//
// caP'n (Aug 3): "build out some scaffolding first just to make sure it
// works because later as you build more stuff you might get inspired
// and you'll be glad you didn't spend so much time on one animation at
// a time so you can apply what you learned."
//
// He is describing the exact failure mode this file exists to prevent.
// The first three scores get written by hand and they are wonderful.
// The next thirty get written by hand and four of them drift — a brand
// that expands instead of contracting, a Nihil ward in Omnia crimson, a
// Casting that forgot its rune. By card sixty the vocabulary has no
// grammar left and the board reads as noise, which is the precise
// opposite of the ask.
//
// ── SO: THE GRAMMAR IS CODE, NOT DISCIPLINE ────────────────────────
// The things that must be true of EVERY card of a class are functions
// here, not conventions in a comment. An Impera cannot forget to press
// inward, because `brand()` is the only way to write one. A Tome cannot
// forget its school's colour, because `cast()` takes the school and
// looks it up. What each card gets to choose is what makes it ITSELF —
// and that is the part worth spending the tokens on.
//
//   the class says WHAT KIND of thing this is      → the builder
//   the card says WHAT THIS ONE IS                 → the author
//
// ── THE THREE-BEAT LAW ─────────────────────────────────────────────
// Every score in this game reads in three beats, and a score that skips
// one feels broken even when nobody can say why:
//
//   TELL     something is about to happen        (0 → ~25%)
//   TURN     the thing happens                   (~25 → ~60%)
//   TOLL     the world acknowledges it           (~60 → 100%)
//
// The floor primitives map onto it cleanly: a rune/pillar/spiral TELLS,
// a beam/orb/slash/sever TURNS, and a shock/crack/motes TOLLS. Most of
// the dead-feeling animations in any game are missing the TOLL — the
// hit lands and the world says nothing back.
//
// ── EVERY LAW ABOVE THIS FILE STILL BINDS ──────────────────────────
// Scores are DATA. They are pure, they are total, they never throw, and
// deleting every one of them leaves a duel that resolves identically
// and merely looks like it did in July.
// ═══════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════
// PART I — THE PALETTE
// ═══════════════════════════════════════════════════════════════════
// Local hex on purpose — same law as strikeLanguage's and cardMotion's,
// so a score file drops into another IE title without dragging Terror's
// chrome behind it. Where a hue matches SCHOOL_COLORS_SD / TYPE_COLORS_SD
// it matches DELIBERATELY: the language and the chrome speak one colour
// law, and if theme.js ever moves a hue it moves here too, by hand.

export const P = {
  // ── The four Ilks. The spine of the whole palette. ──────────────
  space: "#22d3ee", phantom: "#c084fc", dragon: "#f87171", time: "#fbbf24",

  // ── Space family ────────────────────────────────────────────────
  starWhite: "#dbeeff", starCore: "#9fd4ff", cyanHot: "#67e8f9",
  scalpel: "#dff6ff", voidBlue: "#38bdf8", nebula: "#7dd3fc",

  // ── Phantom family ──────────────────────────────────────────────
  violetPale: "#d8b4fe", violetDeep: "#7c3aed", amethyst: "#a855f7",
  violetIce: "#e9d5ff", shadow: "#6d28d9",

  // ── Time family ─────────────────────────────────────────────────
  emberGold: "#fde68a", judgeGold: "#fff6d8", swarm: "#fcd34d",
  brass: "#d4a72c", rust: "#f59e0b",

  // ── Dragon family ───────────────────────────────────────────────
  crimson: "#ef4444", roseSoft: "#fca5a5", bloodDeep: "#b91c1c",
  scale: "#fb7185",

  // ── The eight schools (in sync with TOME_SCHOOLS) ───────────────
  gold: "#e8b84b",        // Construction — economy, permanence
  goldPale: "#f5d878",    // Treasure — equipment
  white: "#f2f6ff",       // Nihil — the refusal
  omnia: "#dc2626",       // Omnia — the ruin
  sorcery: "#a855f7",     // Ancient Sorcery — the search
  ritual: "#c084fc",      // Ritual — recursion
  jynxi: "#2ee66b",       // Shadow Gambling — chance, and she is watching
  contract: "#fb923c",    // Contract — negotiation

  // ── The four Affinities ─────────────────────────────────────────
  holy: "#f5d878", evil: "#dc2626", awoken: "#5eead4", jest: "#2ee66b",

  // ── Board-level ─────────────────────────────────────────────────
  vellum: "#e8d5a3", bone: "#a8896a", indigo: "#818cf8",
  griefViolet: "#a78bfa", returnGreen: "#86efac", returnCore: "#4ade80",
  animaGreen: "#4ade80", animaRed: "#ff6b6b", khaoBlue: "#38bdf8",
  teal: "#5eead4", ink: "#1e1b2e",
};

/** Ilk → its anchor hue. Total: an unknown Ilk gets vellum, never undefined. */
export const ilkHue = (ilk) => (
  { Space: P.space, Phantom: P.phantom, Dragon: P.dragon, Time: P.time }[ilk] || P.vellum
);

/** Tome school → { color, sigil }. Total, same contract. */
export const SCHOOL = {
  Construction:      { color: P.gold,     sigil: "▦" },
  Treasure:          { color: P.goldPale, sigil: "✦" },
  Nihil:             { color: P.white,    sigil: "⊘" },
  Omnia:             { color: P.omnia,    sigil: "✖" },
  "Ancient Sorcery": { color: P.sorcery,  sigil: "❋" },
  Ritual:            { color: P.ritual,   sigil: "⛧" },
  "Shadow Gambling": { color: P.jynxi,    sigil: "⚄" },
  Contract:          { color: P.contract, sigil: "⚖" },
};
export const schoolOf = (ilk) => SCHOOL[ilk] || { color: P.vellum, sigil: "✧" };

export const ALIGN = {
  Holy:     { color: P.holy,   sigil: "✦" },
  Evil:     { color: P.evil,   sigil: "✖" },
  Awoken:   { color: P.awoken, sigil: "◉" },
  "J'ynxi": { color: P.jest,   sigil: "⚄" },
};

// ═══════════════════════════════════════════════════════════════════
// PART II — THE CUE SHORTHANDS
// ═══════════════════════════════════════════════════════════════════
// One line each, and every one of them is just an object literal with
// sane defaults. They exist so a score reads as MUSIC — a list of what
// happens and when — instead of as a wall of property bags. Compare:
//
//   { fx: "bloom", at: 220, ms: 300, to: "target", r: 1.1, color: X }
//   bloom(220, X, { r: 1.1 })
//
// Same object, and the second one you can scan forty of.
//
// Every builder takes (at, color, opts) and every one of them merges
// `opts` LAST, so any score can override any default without needing a
// new builder. That is the Exception Law in its cheapest form: the
// escape hatch is already there, for free, on all twenty-seven.

const mk = (fx) => (at, color, o) => ({ fx, at, color, to: "target", ...o });

// The originals
export const beam    = mk("beam");
export const orb     = mk("orb");
export const slash   = mk("slash");
export const shock   = mk("shock");
export const motes   = mk("motes");
export const pillar  = mk("pillar");
export const spiral  = mk("spiral");
export const bolt    = mk("bolt");
export const wave    = mk("wave");
export const bloom   = mk("bloom");
export const stream  = mk("stream");
export const glyph   = mk("glyph");
export const grave   = mk("grave");
// The fourteen more
export const rune    = mk("rune");
export const veil    = mk("veil");
export const shatter = mk("shatter");
export const chain   = mk("chain");
export const coin    = mk("coin");
export const crack   = mk("crack");
export const vortex  = mk("vortex");
export const embers  = mk("embers");
export const lattice = mk("lattice");
export const halo    = mk("halo");
export const star    = mk("star");
export const rift    = mk("rift");
export const echoes  = mk("echoes");
export const sever   = mk("sever");
// The last five (H24 pass 3)
export const constellation = mk("constellation");
export const mirror  = mk("mirror");
export const plume   = mk("plume");
export const thorn   = mk("thorn");
export const scan    = mk("scan");

/**
 * Build a score. `ms` defaults to the last cue's end.
 *
 * `opts.shake` — { mag, ms, at } — kicks the WHOLE WORLD, not a card.
 *
 * ── WHY THIS IS A SCORE PROPERTY AND NOT A 28TH PRIMITIVE ──────────
 * Every primitive draws something at a place. A shake draws nothing and
 * has no place — it moves the camera, which is a property of the whole
 * board and of which there can only ever be one at a time. Modelling it
 * as a cue would mean a cue that renders nothing, mounts nothing, and
 * fights every other copy of itself for a single global. So it rides
 * the score instead, and `fireScore` hands it to the FX bus that the
 * Dragon's Maul has been using since Ship 6.
 *
 * ── AND WHY ALMOST NOTHING USES IT ─────────────────────────────────
 * H21's rule, kept exactly: only Lord-weight Dragons may kick the
 * world, "anything less and the board shakes constantly, which reads as
 * jitter rather than weight." The same logic bounds this. A shake is
 * the loudest tool available and it is spent on maybe a dozen moments
 * in the entire game — the two 10-Khao-and-10-Anima U'hltis, a Soul
 * being cut, a Warlock losing eight Anima in one hit, and the end of
 * the Solrend. If it ever fires twice in a normal turn, it is wrong.
 *
 * `mag` is in world units and the honest ceiling is ~0.17 (the value
 * the existing Anima thunder caps at). Above that the table looks
 * broken rather than struck.
 */
export function score(name, cues, ms, opts) {
  const end = cues.reduce((m, c) => Math.max(m, (c.at || 0) + (c.ms || 400)), 0);
  const s = { name, ms: ms || end, cues };
  if (opts && opts.shake) s.shake = opts.shake;
  if (opts && opts.surge) s.surge = opts.surge;
  return s;
}

/** A world-kick, for the handful of moments that have earned one. */
export const SHAKE = {
  /** A Soul cut, a heavy bleed. */
  hard: (at = 0) => ({ mag: 0.075, ms: 420, at }),
  /** A board-wide execution. */
  heavy: (at = 0) => ({ mag: 0.11, ms: 520, at }),
  /** Show Stomper and I or None. Nothing else, ever. */
  world: (at = 0) => ({ mag: 0.16, ms: 640, at }),
};

/**
 * ── THE SURGE ──────────────────────────────────────────────────────
 * The FIELD takes the score's colour for a beat — the seam, and a wash
 * across the whole felt (`FieldAtmos3D`'s `SurgeWash`).
 *
 * A shake says *that was violent*. A surge says *that was important*,
 * and the two are genuinely different claims: Sweet Dreams surges and
 * never shakes, because nothing is damaged and everything has changed.
 *
 * Far more scores may surge than may shake — a shake interrupts the
 * player's ability to read the board and a surge does not — but it is
 * still spent, not sprinkled. Every U'hlti, every Affinity, and the
 * moments a Solrend turns on. Never a Tome, never a summon, never an
 * Impera below the two that end boards.
 *
 * ⚠ THE COLOUR IS THE SCORE'S OWN. Passing a hue that isn't in the
 * score is how a Nihil ward ends up washing the table in Omnia crimson
 * — the exact colour-law break the whole palette exists to prevent.
 */
export const SURGE = {
  /** A Signature resolving, an Affinity sealing. */
  soft: (hue, at = 0) => ({ hue, mag: 0.45, ms: 820, at }),
  /** A board-wide effect, a Soul cut, a heavy bleed. */
  full: (hue, at = 0) => ({ hue, mag: 0.68, ms: 950, at }),
  /** The Solrend turning. Victory, defeat, a Prophecy fulfilled. */
  total: (hue, at = 0) => ({ hue, mag: 0.9, ms: 1300, at }),
};

// ═══════════════════════════════════════════════════════════════════
// PART III — THE CLASS GRAMMARS
// ═══════════════════════════════════════════════════════════════════
// The part that keeps 180 scores speaking one language. Each of these
// returns the cues that are TRUE OF THE WHOLE CLASS; the author appends
// what makes the individual card itself.

/**
 * THE BRAND — every Impera, forever.
 *
 * H23's law, and it is not negotiable: "a brand presses INWARD, always,
 * and that consistency is what will make thirty-six of them read as one
 * class of thing." A contracting iris (shock with size > r, which the
 * primitive reads as closing) plus the settle bloom. 220ms, cheap, and
 * it is the first thing the eye sees on every one of the 36.
 */
export const brand = (color, at = 0, o = {}) => ([
  shock(at, color, { to: "self", ms: o.ms || 380, size: o.size || 1.8, r: 0.4, tone: 0.85 }),
  bloom(at + 140, color, { to: "self", ms: 280, r: o.r || 0.72, tone: 0.8 }),
]);

/**
 * THE SIGNATURE — every U'hlti, forever.
 *
 * "It should feel like a war crime." Twelve exist; they cost Khao AND
 * Anima, the only class that charges both. So the opening is the
 * loudest in the game by a wide margin and it is allowed to be: a
 * column, a rising coil, and the Lord's own mark. Every U'hlti score
 * then spends its second half doing something nothing else can.
 */
export const signature = (color, accent, at = 0) => ([
  pillar(at, color, { to: "self", ms: 700, r: 6.2, size: 0.56, spin: 0.7 }),
  spiral(at + 90, accent, { to: "self", ms: 700, tone: "up", spin: 3, r: 0.82, size: 3.1 }),
  halo(at + 300, accent, { to: "self", ms: 620, r: 1.0, size: 0.8, spin: 1.6 }),
]);

/**
 * THE CASTING — every Tome, forever.
 *
 * "The page opens and something answers." The rune is the page; the
 * school picks its hue and its mark. Tick count rides the Tome's Level
 * so a Lvl 3 casting is visibly more elaborate than a Lvl 1 — which is
 * a free read on card weight the player never has to be taught.
 */
export const casting = (ilk, level = 1, at = 0) => {
  const s = schoolOf(ilk);
  return [
    rune(at, s.color, { to: "self", ms: 620, r: 1.05, n: 4 + level * 3, spin: 0.5 }),
    glyph(at + 200, s.color, { to: "self", ms: 460, tone: s.sigil, size: 26 + level * 6, r: 0.5 }),
  ];
};

/**
 * THE SEAL — every Affinity, forever.
 *
 * Four exist, ever. Khao cost zero: "it costs nothing but EVERYTHING
 * BEFORE IT." And the shape of the top of the ladder is not more
 * damage, it is more DENIAL — so the ring runs INWARD (`tone: "close"`)
 * and the halo stays up after, because an Affinity is a status, not an
 * event.
 */
export const seal = (align, at = 0) => {
  const a = ALIGN[align] || ALIGN.Holy;
  return [
    rune(at, a.color, { to: "self", ms: 700, r: 2.1, n: 16, spin: 0.3, tone: "close" }),
    halo(at + 260, a.color, { to: "self", ms: 820, r: 1.15, size: 0.85, spin: 1.1 }),
    glyph(at + 340, a.color, { to: "self", ms: 520, tone: a.sigil, size: 46, r: 0.7 }),
  ];
};

/**
 * THE TOLL — the world acknowledges it.
 *
 * The third beat, and the one that is skipped by accident more than any
 * other. A hit that produces no ring is a card that changed colour.
 * `weight` 1–3 scales it: a Lvl 1 Tome and a Lord's U'hlti do not toll
 * the same. Two rings at weight 3 — the second wider and quieter, which
 * is what an aftermath sounds like.
 */
export const toll = (color, at, weight = 1, anchor = "target") => {
  const cues = [
    shock(at, color, { to: anchor, ms: 380 + weight * 60, size: 0.18 + weight * 0.03,
                       r: 1.2 + weight * 0.5, tone: 0.75 }),
  ];
  if (weight >= 3) {
    cues.push(shock(at + 120, color, { to: anchor, ms: 560, size: 0.38,
                                       r: 3.4, tone: 0.4 }));
  }
  return cues;
};

/** THE WOUND — bloom + toll, the two beats every impact needs. */
export const wound = (color, at, weight = 1, anchor = "target") => ([
  bloom(at, color, { to: anchor, ms: 280, r: 0.85 + weight * 0.22 }),
  ...toll(color, at + 20, weight, anchor),
]);

// ── THE CROSSING WAS HERE, AND IT IS GONE ON PURPOSE ──────────────
// A `crossing()` helper (launch → arrive → toll) was written during
// pass 1 on the theory that "something travels the seam and arrives"
// would repeat constantly. It was exported, documented, and used by
// exactly ZERO of the 251 scores — because every card that sends a
// thing across the table sends a DIFFERENT thing, and the shared part
// (`wound`) was already its own builder.
//
// That is the Exception Law's own warning, in miniature: "don't
// manufacture exceptions to look flexible... The test is concrete: can
// I name the thing this unlocks? If no, ship the simple version." I
// could not name one, so it is deleted rather than left sitting in the
// kit looking load-bearing to whoever reads this next.

// ═══════════════════════════════════════════════════════════════════
// PART IV — THE LINTER
// ═══════════════════════════════════════════════════════════════════
// 180 scores is enough that a handful WILL be wrong, and every failure
// mode here is silent at runtime: a cue whose `at` lands past the
// score's own `ms` simply never plays, and nothing anywhere complains.
// You would find it by noticing a card feels flat, which is not a way
// to find anything.
//
// So: a pure function that audits a registry and returns findings. The
// duel path never calls it — the bench and the test harness do.

export const FX_VOCAB = [
  "beam", "orb", "slash", "shock", "motes", "pillar", "spiral", "bolt",
  "wave", "bloom", "stream", "glyph", "grave", "rune", "veil", "shatter",
  "chain", "coin", "crack", "vortex", "embers", "lattice", "halo",
  "star", "rift", "echoes", "sever",
  "constellation", "mirror", "plume", "thorn", "scan",
];

const ANCHORS = ["self", "target", "seam", "sky", "selfsky", "back"];

/**
 * Audit one registry. Returns [] when clean.
 * Findings are {key, level, msg} — "error" is a cue that will not play,
 * "warn" is a cue that will play but probably not as intended.
 */
export function lintRegistry(reg, label = "registry") {
  const out = [];
  const add = (key, level, msg) => out.push({ key: `${label}:${key}`, level, msg });
  Object.keys(reg || {}).forEach((key) => {
    const s = reg[key];
    if (!s || typeof s !== "object") return add(key, "error", "not a score object");
    if (!s.name) add(key, "warn", "no name (the Codex will show a blank row)");
    if (!Array.isArray(s.cues) || !s.cues.length) return add(key, "error", "no cues");
    let last = 0;
    s.cues.forEach((c, i) => {
      const at = c.at || 0;
      const ms = c.ms || 400;
      if (!c.fx) return add(key, "error", `cue ${i} has no fx`);
      if (FX_VOCAB.indexOf(c.fx) === -1) add(key, "error", `cue ${i} unknown fx "${c.fx}"`);
      if (!c.color) add(key, "warn", `cue ${i} (${c.fx}) has no color`);
      [c.from, c.to].forEach((tok) => {
        if (tok != null && typeof tok === "string" && ANCHORS.indexOf(tok) === -1) {
          add(key, "error", `cue ${i} unknown anchor "${tok}"`);
        }
      });
      if (at < 0) add(key, "error", `cue ${i} has a negative at`);
      last = Math.max(last, at + ms);
    });
    // THE SILENT KILLER — but state it precisely, because the obvious
    // version of this rule is wrong and fires on healthy scores.
    //
    // A score's `ms` is what the TURN CLOCK is held for, not when the
    // last photon dies. A ring still fading out while the board moves
    // on is correct and desirable: holding the clock for a decorative
    // tail steals time from the player for nothing (T17's whole
    // complaint — "don't want people wasting their turn watching
    // animations"). So a modest overrun is fine.
    //
    // What is NEVER fine is a cue that STARTS after the hold has
    // expired. That one plays into a board that has already changed —
    // the wound lands on a card that is gone, the seal closes over an
    // empty rift — and it is completely silent in review.
    const startsLate = s.cues.filter((c) => s.ms && (c.at || 0) > s.ms);
    if (startsLate.length) {
      add(key, "error",
        `${startsLate.length} cue(s) start after the ${s.ms}ms hold — they play into a changed board`);
    }
    if (s.ms && last > s.ms * 1.5) {
      add(key, "warn", `cues run ${last}ms against a ${s.ms}ms hold — tail is longer than the score`);
    }
    if (s.ms && s.ms > 2600) {
      add(key, "warn", `${s.ms}ms is long enough to feel like a cutscene`);
    }

    // ── DENSITY, NOT COUNT ────────────────────────────────────────
    // The first version of this rule counted cues, and it was wrong in
    // the way that matters: it flagged every U'hlti and no Impera, when
    // the U'hltis are the SPARSER scores. A 2400ms Signature with 13
    // cues is 5.4 cues/second; a 700ms brand with 6 is 8.6. The long
    // one has room to breathe and the short one does not.
    //
    // What actually reads as noise is SIMULTANEITY — how many separate
    // things are on screen in the same instant. Two is a chord. Six is
    // a smear, and no amount of authoring intent survives it.
    const events = [];
    s.cues.forEach((c) => {
      events.push([c.at || 0, 1]);
      events.push([(c.at || 0) + (c.ms || 400), -1]);
    });
    events.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
    let live = 0, peak = 0, peakAt = 0;
    events.forEach(([at, d]) => {
      live += d;
      if (live > peak) { peak = live; peakAt = at; }
    });
    if (peak > 6) {
      add(key, "warn", `${peak} cues live at once around ${peakAt}ms — reads as a smear, not a chord`);
    }
  });
  return out;
}

/** Lint several registries at once. */
export function lintAll(map) {
  return Object.keys(map).reduce((acc, k) => acc.concat(lintRegistry(map[k], k)), []);
}

const motionKit = {
  P, ilkHue, SCHOOL, schoolOf, ALIGN,
  beam, orb, slash, shock, motes, pillar, spiral, bolt, wave, bloom,
  stream, glyph, grave, rune, veil, shatter, chain, coin, crack,
  vortex, embers, lattice, halo, star, rift, echoes, sever,
  constellation, mirror, plume, thorn, scan,
  score, brand, signature, casting, seal, toll, wound,
  FX_VOCAB, lintRegistry, lintAll,
};
export default motionKit;
